/** Pessoa mencionada num comentário (id e nome no momento — DEC-048). */
export type MentionRef = { id: string; name: string };

/**
 * O "@algo" que está sendo digitado antes do cursor: começa no início do texto ou depois de um espaço e não passa
 * de uma linha. Devolve onde começa o "@" e o que vem depois (a busca).
 */
export function mentionQueryAt(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const match = /(^|\s)@([^\n@]{0,40})$/.exec(before);
  if (!match) return null;
  const query = match[2] ?? '';
  if (query.startsWith(' ')) return null;
  return { start: match.index + (match[1]?.length ?? 0), query };
}

/** Troca o "@busca" por "@Nome " e devolve o texto novo e onde o cursor fica. */
export function insertMention(
  text: string,
  start: number,
  caret: number,
  name: string,
): { text: string; caret: number } {
  const inserted = `@${name} `;
  const after = text.slice(caret).replace(/^ /, '');
  return { text: `${text.slice(0, start)}${inserted}${after}`, caret: start + inserted.length };
}

/** Só as menções cujo "@Nome" continua no texto (a pessoa pode ter apagado depois de escolher). */
export const mentionsInText = (text: string, mentions: MentionRef[]) =>
  mentions.filter((m, i) => text.includes(`@${m.name}`) && mentions.findIndex((x) => x.id === m.id) === i);

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Divide o comentário em trechos de texto e de menção (para destacar "@Nome" das pessoas mencionadas). */
export function splitMentions(
  text: string,
  mentions: MentionRef[],
): { text: string; mention?: MentionRef }[] {
  if (!mentions.length) return [{ text }];
  const byName = new Map(mentions.map((m) => [m.name, m]));
  const names = [...byName.keys()].sort((a, b) => b.length - a.length).map(escapeRegExp);
  const pattern = new RegExp(`@(${names.join('|')})`, 'g');
  const parts: { text: string; mention?: MentionRef }[] = [];
  let last = 0;
  for (const match of text.matchAll(pattern)) {
    if (match.index > last) parts.push({ text: text.slice(last, match.index) });
    parts.push({ text: match[0], mention: byName.get(match[1]!)! });
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts;
}
